/**
 * account-display — pure display judgements for Composio connected accounts.
 *
 * The wire's per-account rows keep `alias: null` until the identity probe names
 * them, and the server's positional fallback ("gmail #2") is lowercase slug
 * text. Every user-facing surface routes through here so labels read premium
 * ("Gmail #2", "Needs reconnecting") and every surface says it the same way.
 *
 * accounts[] arrives server-ordered ACTIVE first then newest (accounts[0] is
 * the primary) — positional labels count off that order, matching the server's
 * own displayAccountName counting. Never sort locally.
 */
import type { ConnectedAccountWire } from "@bash/shared";
import { accountNeedsReconnect } from "@bash/shared";
import { getIntegrationDisplayName } from "@/lib/composio-tools";

/** "gmail #2" (server positional fallback) → "Gmail #2". Anything else passes through. */
export function formatAccountName(name: string | null): string | null {
  if (!name) return name;
  const m = /^([a-z0-9_-]+) #(\d+)$/.exec(name);
  if (!m) return name;
  return `${getIntegrationDisplayName(m[1])} #${m[2]}`;
}

/**
 * Label for one account: its name, else the day it was added.
 *
 * The old fallback was positional ("Gmail #2"), which had two problems worth
 * naming because they are the reason this changed. It told the user nothing —
 * an index into a list they cannot see — and it was UNSTABLE: removing a sibling
 * silently renamed every account after it, so the name a user learned stopped
 * matching the account it named.
 *
 * `created_at` is on every account, always, and it says something true. On the
 * rare tie (two accounts added the same day, neither named) the id tail
 * disambiguates rather than a counter, for exactly the stability reason above:
 * an id tail is the same string forever, a counter is not.
 *
 * `accounts` is the toolkit's full server-ordered list and is only read for the
 * tie check; pass it whenever you have it.
 */
export function accountRowLabel(
  account: ConnectedAccountWire,
  _index: number,
  accounts?: readonly ConnectedAccountWire[]
): string {
  if (account.alias) return account.alias;
  const added = addedOn(account.created_at);
  const tied = accounts?.some(
    (a) => a.id !== account.id && !a.alias && addedOn(a.created_at) === added
  );
  return tied ? `${added} · ${account.id.slice(-4)}` : added;
}

/** "Added 12 Aug", or "Added 12 Aug 2025" once the year stops being obvious.
 *  An unparseable timestamp must never render "Invalid Date" at a user. */
function addedOn(createdAt: string): string {
  const d = new Date(createdAt);
  if (Number.isNaN(d.getTime())) return "Added earlier";
  const sameYear = d.getFullYear() === new Date().getFullYear();
  return `Added ${d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
  })}`;
}

/**
 * The card's one-line verdict over a whole roster (never off accounts[0]).
 *
 * Framed by what WORKS, not what is broken: the hollow coins beside it already
 * say how many are signed out, and a card whose loudest line counts failures
 * reads as an alarm every time you open the page. `tone` still goes warn when
 * anything needs the user, so the card flags itself without leading with it.
 *
 * Ordering is dead-beats-pending-beats-healthy: an account that needs a new
 * consent is the only one the user can act on, so it wins the line.
 */
export function rosterStateLine(accounts: readonly ConnectedAccountWire[]): {
  text: string;
  tone: "connected" | "warn" | "pending";
} {
  const signedIn = accounts.filter((a) => a.status === "active").length;
  const anyDead = accounts.some((a) => accountNeedsReconnect(a.status));
  if (anyDead) {
    return signedIn === 0
      ? { text: "signed out", tone: "warn" }
      : { text: `${signedIn} signed in`, tone: "warn" };
  }
  if (signedIn === 0) return { text: "finishing setup", tone: "pending" };
  // One healthy account is just "connected" — a count of one is a count nobody needs.
  return signedIn === 1
    ? { text: "connected", tone: "connected" }
    : { text: `${signedIn} signed in`, tone: "connected" };
}

export type AccountTone = "success" | "warning" | "pending";

/** Premium status word for an account row. Never the raw enum. */
export function accountStatusLabel(
  status: ConnectedAccountWire["status"]
): string {
  if (status === "active") return "Connected";
  if (accountNeedsReconnect(status)) return "Needs reconnecting";
  return "Finishing setup";
}

export function accountStatusTone(
  status: ConnectedAccountWire["status"]
): AccountTone {
  if (status === "active") return "success";
  if (accountNeedsReconnect(status)) return "warning";
  return "pending";
}

/** One-line label for compact surfaces (ManualScanPicker): ACTIVE account
 *  labels joined with a middot; flag-off wire falls back to the row name. */
export function accountsSummary(conn: {
  account_name: string | null;
  accounts?: ConnectedAccountWire[];
}): string | null {
  if (!conn.accounts) return formatAccountName(conn.account_name);
  const rows = conn.accounts;
  const labeled = rows.map((a, i) => ({
    a,
    label: accountRowLabel(a, i, rows),
  }));
  const active = labeled.filter((x) => x.a.status === "active");
  if (active.length === 0) return formatAccountName(conn.account_name);
  return active.map((x) => x.label).join(" · ");
}

/** A hint that is a raw connected-account id means nothing to a human —
 *  render only human-shaped hints. (PR 2's runner should prefer aliases.) */
export function renderableAccountHint(
  hint: string | null | undefined
): string | null {
  const trimmed = hint?.trim();
  if (!trimmed) return null;
  return /^ca_/i.test(trimmed) ? null : trimmed;
}

/** "work" + "Gmail" → "your work Gmail"; "a@b.com" + "Gmail" → "Gmail (a@b.com)";
 *  no renderable hint → the plain display name. */
export function hintedAppLabel(
  hint: string | null | undefined,
  displayName: string
): string {
  const h = renderableAccountHint(hint);
  if (!h) return displayName;
  return h.includes("@") ? `${displayName} (${h})` : `your ${h} ${displayName}`;
}
